import * as React from 'react';
import styled from 'styled-components';

import { WoltInboundLoad as InboundLoadT, WoltInboundLoads } from 'common/api-types/warehouse';
import { useScrollToPreviousPosition } from 'common/hooks/useScrollToPreviousPosition';

import { InboundLoad } from './InboundLoad';
import { InboundLoadDateHeader } from './InboundLoadDateHeader';
import { useExpandInboundLoads } from './useExpandInboundLoads';

const Root = styled.div`
  display: flex;
  flex-direction: column;
  overflow: auto;
`;

type Props = {
  inboundLoads: WoltInboundLoads;
  onItemClick: (itemId: string, inboundLoadId: string) => void;
  itemSearchResult?: boolean;
};

const getGroupedInboundLoads = (inboundLoads: WoltInboundLoads) =>
  inboundLoads.reduce((acc: Record<string, WoltInboundLoads>, next) => {
    const { expectedDeliveryStartDate: date } = next;
    const grouping = acc[date] ? [...acc[date], next] : [next];
    return { ...acc, [date]: grouping };
  }, {});

const getTotalPosAndUnits = (inboundLoads: WoltInboundLoads) => {
  const totalPOs = inboundLoads.length;
  const totalUnits = inboundLoads.reduce(
    (sum, inboundLoad) => sum + inboundLoad.totalOrderedQuantity.value,
    0,
  );
  return { totalPOs, totalUnits };
};

export const InboundLoadResults = ({
  inboundLoads,
  onItemClick,
  itemSearchResult = false,
}: Props) => {
  const ref = useScrollToPreviousPosition();

  const groupedInboundLoads = getGroupedInboundLoads(inboundLoads);

  useExpandInboundLoads(inboundLoads, itemSearchResult);

  return (
    <Root ref={ref}>
      {Object.keys(groupedInboundLoads).map((groupKey) => {
        const inboundLoads = groupedInboundLoads[groupKey];
        const { totalPOs, totalUnits } = getTotalPosAndUnits(inboundLoads);

        return (
          <React.Fragment key={groupKey}>
            <InboundLoadDateHeader
              deliveryDate={groupKey}
              totalUnits={totalUnits}
              totalPos={totalPOs}
            />
            {inboundLoads.map((inboundLoad: InboundLoadT, index) => (
              <InboundLoad
                key={inboundLoad.id}
                inboundLoad={inboundLoad}
                onItemClick={onItemClick}
                poIndex={index}
              />
            ))}
          </React.Fragment>
        );
      })}
    </Root>
  );
};
